import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowLeft, XCircle, Clock, Mail } from "lucide-react";
import { getCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Success() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({
        status: sessionId ? "checking" : "missing",
        payment_status: null,
        amount_total: 0,
        currency: "eur",
    });
    const attempts = useRef(0);
    const { clear } = useCart();

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            attempts.current += 1;
            // Poll up to 15x — proxy retrieve is intermitente; a reconciliação
            // do backend confirma mesmo que o utilizador feche a página.
            if (attempts.current > 15) {
                setState((s) => ({ ...s, status: "processing" }));
                return;
            }
            try {
                const data = await getCheckoutStatus(sessionId);
                if (cancelled) return;
                setState(data);
                if (data.payment_status === "paid") {
                    clear();
                    return;
                }
                if (data.status === "expired") return;
                setTimeout(poll, 2000);
            } catch {
                // 502 is expected on emergent test proxy — degrade gracefully
                setTimeout(poll, 2500);
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    const isPaid = state.payment_status === "paid";
    const isExpired = state.status === "expired";
    const isMissing = state.status === "missing";
    const isProcessing = state.status === "processing";

    return (
        <div className="min-h-screen bg-brand-cream flex items-center justify-center p-6">
            <div
                className="max-w-lg w-full bg-white rounded-2xl shadow-pop p-8 md:p-12 text-center"
                data-testid="checkout-success-page"
            >
                {isPaid ? (
                    <>
                        <div className="mx-auto h-20 w-20 rounded-full bg-brand-green-soft flex items-center justify-center">
                            <CheckCircle2 className="h-10 w-10 text-brand-green" />
                        </div>
                        <h1
                            className="mt-6 font-display font-medium text-3xl md:text-4xl text-brand-ink"
                            data-testid="success-status-paid"
                        >
                            Pagamento confirmado!
                        </h1>
                        <p className="mt-3 text-brand-ink-soft">
                            Obrigado pela tua compra. Já recebemos o teu pedido e vais receber a
                            confirmação por email.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-green-soft px-4 py-2 text-sm">
                            <span className="text-brand-ink-soft">Total:</span>
                            <span className="font-bold text-brand-ink">
                                {state.amount_total?.toFixed(2).replace(".", ",")} €
                            </span>
                        </div>
                    </>
                ) : isExpired ? (
                    <>
                        <div className="mx-auto h-20 w-20 rounded-full bg-red-50 flex items-center justify-center">
                            <XCircle className="h-10 w-10 text-red-500" />
                        </div>
                        <h1 className="mt-6 font-display font-medium text-3xl text-brand-ink">
                            Sessão expirada
                        </h1>
                        <p className="mt-3 text-brand-ink-soft">
                            A sessão de pagamento expirou. Tenta novamente.
                        </p>
                    </>
                ) : isMissing ? (
                    <>
                        <div className="mx-auto h-20 w-20 rounded-full bg-brand-orange-soft flex items-center justify-center">
                            <Mail className="h-10 w-10 text-brand-orange" />
                        </div>
                        <h1 className="mt-6 font-display font-medium text-3xl text-brand-ink">
                            Sessão não encontrada
                        </h1>
                        <p className="mt-3 text-brand-ink-soft">
                            Não conseguimos encontrar uma sessão de pagamento. Volta ao carrinho
                            para iniciar uma nova compra.
                        </p>
                    </>
                ) : isProcessing ? (
                    <>
                        <div className="mx-auto h-20 w-20 rounded-full bg-brand-orange-soft flex items-center justify-center">
                            <Clock className="h-10 w-10 text-brand-orange" />
                        </div>
                        <h1
                            className="mt-6 font-display font-medium text-3xl text-brand-ink"
                            data-testid="success-status-processing"
                        >
                            Pagamento em processamento
                        </h1>
                        <p className="mt-3 text-brand-ink-soft">
                            Recebemos o teu pedido. Vais receber o email de confirmação assim que o
                            pagamento for validado pelo nosso sistema.
                        </p>
                    </>
                ) : (
                    <>
                        <Loader2 className="mx-auto h-12 w-12 text-brand-green animate-spin" />
                        <h1
                            className="mt-6 font-display font-medium text-2xl text-brand-ink"
                            data-testid="success-status-checking"
                        >
                            A verificar pagamento...
                        </h1>
                        <p className="mt-3 text-brand-ink-soft text-sm">
                            Não feches esta página.
                        </p>
                    </>
                )}

                <Link
                    to="/"
                    data-testid="success-back-home"
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-brand-green text-white px-6 py-3 text-sm font-semibold transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" /> Voltar à loja
                </Link>
            </div>
        </div>
    );
}
